import CheckIcon from "@mui/icons-material/Check";
import { chipClasses, List, Popover, Stack, Tooltip } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Dialog from "@hyro-components/Dialogs/HyroUiDialog";

import { DIALOG_VARIANTS, EXIT_ASSISTANT_DIALOG } from "@hyro-components/Dialogs/DialogConsts";
import { useCurrentAssistantPage } from "@hooks";
import UnfoldIcon from "../icons/UnfoldIcon";
import useAppStore from "../stores/appStore";
import {
  selectedAssistantSelector,
  setSelectedAccountIdSelector,
  clearAgentSelector,
  systemModeSelector
} from "../stores/selectors/appSelectors";
import { setSelectedCollectionSelector } from "../stores/selectors/collectionsSelectors";
import { isEditModeSelector } from "../stores/selectors/tripletsSelectors";
import { SYSTEM_MODES } from "../stores/slices/createAppSlice";
import { clearPreviewStateSelector } from "../stores/selectors/previewSelectors";
import { ASSISTANT_TYPE_TO_METADATA, BUILDING_BLOCKS, CHANNEL_TO_DISPLAY_NAME, ENGAGEMENT_TYPES, SKILLS } from "@consts";
import { capitalizeString, getAgentIcon } from "@utils";
import SearchBar from "@hyro-components/SearchBar";
import { noOptionList } from "src/components/collections/helpers";
import useKeyboardNavigation from "@hyro-components/hooks/useKeyboardNavigation";
import { useDraft } from "src/queries/hooks/assistants";
import Chip from "@hyro-components/Chip";
import { useFetchAccountAssistants } from "src/queries/hooks/accounts";
import { deleteInsightsDashboardKey } from "@hyro-components/ResponsiveIframe/ResponsiveIframe.utils";

const ButtonContainer = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5)
}));

const StyledButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== "hover"
})(({ theme, hover }) => ({
  "maxWidth": "100%",
  "padding": theme.spacing(1, 1.5),
  "borderRadius": theme.shape.borderRadius,
  "display": "flex",
  "justifyContent": "center",
  "transition": "none",
  "&:hover": hover
    ? {
      backgroundColor: theme.palette.system[50]
    }
    : {
      backgroundColor: "transparent",
      cursor: "default"
    },
  "&.Mui-selected": {
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.tertiary[300]
  }
}));

const StyledButtonIcon = styled(ListItemIcon)(() => ({
  minWidth: 0,
  svg: {
    verticalAlign: "top"
  }
}));

const ChannelListItem = styled(ListItemText)(() => ({
  marginBlock: 0,
  display: "flex",
  alignItems: "center",
  marginTop: "2px"
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  display: "block",
  marginBlock: theme.spacing(1),
  padding: theme.spacing(0, 1)
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== "selected" && prop !== "focused"
})(({ selected, theme }) => ({
  "minHeight": 36,
  "justifyContent": open ? "initial" : "center",
  "gap": theme.spacing(0.5),
  "padding": theme.spacing(1),
  "borderRadius": theme.shape.borderRadius,
  "textOverflow": "ellipsis",
  "&:hover": {
    backgroundColor: theme.palette.system[50]
  },
  ...(selected && {
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.tertiary[300],
    "&:hover": {
      backgroundColor: theme.palette.tertiary[300]
    }
  })
}));

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: prop => prop !== "selected"
})(({ theme, selected }) => ({
  color: selected ? theme.palette.primary[500] : theme.palette.text[700],
  ...(selected && { justifyContent: "end" }),
  minWidth: 0,
  marginLeft: theme.spacing(1),
  svg: {
    verticalAlign: "top"
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  ...theme.typography.caption,
  marginLeft: "4px",
  borderRadius: "4px",
  height: 18,
  padding: "0 6px",
  [`& .${chipClasses.label}`]: {
    padding: 0
  }
}));

const TextContainer = styled("div")(() => ({
  width: "100%",
  textOverflow: "ellipsis",
  overflow: "hidden"
}));

const ChannelText = styled("span")(({ theme, isList, selected }) => ({
  color: (isList && selected) ? theme.palette.primary[500] : theme.palette.text[800]
}));

const StyledIconContainer = styled(Stack)(() => ({
  justifyContent: "center",
  alignItems: "center",
  width: "40px",
  height: "40px",
  borderRadius: "10px",
  border: "1px solid #DEDFFF",
  backgroundColor: "#F3F3FF"
}));

const AssistantSelector = ({ isNavPanelOpen }) => {
  const isEditMode = useAppStore(isEditModeSelector);
  const { accountId } = useParams();
  const { data: draft } = useDraft();
  const isFirstDraft = draft?.isFirstDraft;
  const setSelectedCollection = useAppStore(setSelectedCollectionSelector);
  const systemMode = useAppStore(systemModeSelector);
  const [anchorEl, setAnchorEl] = useState(null);
  const setSelectedAccount = useAppStore(setSelectedAccountIdSelector);
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const clearAssistant = useAppStore(clearAgentSelector);
  const clearPreviewState = useAppStore(clearPreviewStateSelector);
  const navigate = useNavigate();
  const currentPage = useCurrentAssistantPage();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [upcomingAssistantId, setUpcomingAssistantId] = useState(null);
  const [popoverMinWidth, setPopoverMinWidth] = useState(0);
  const [searchText, setSearchText] = useState("");
  const paperRef = useRef(null);
  const { data: assistants = [] } = useFetchAccountAssistants(accountId);

  const filteredAssistants = useMemo(() => assistants.filter(assistant => assistant.name.toLowerCase().includes(searchText.toLowerCase()) || (assistant.id === searchText)),
    [assistants, searchText]);
  let assistantsToMap = searchText === "" ? assistants : filteredAssistants;
  assistantsToMap = assistantsToMap?.length === 0 ? noOptionList() : assistantsToMap;
  const open = Boolean(anchorEl);
  const showSearch = assistants.length > 5;
  const buttonIsClickable = assistants.length > 1 && systemMode !== SYSTEM_MODES.EDIT;

  useEffect(() => {
    if (accountId) {
      setSelectedAccount(accountId);
    }
  }, [accountId, setSelectedAccount]);

  const onItemSelectedFromKeyboard = (selectedItem) => {
    handleSelectedAssistantChange(selectedItem.id);
  };

  const {
    focusedIndex,
    handleKeyDown,
    listRef,
    resetFocus
  } = useKeyboardNavigation(filteredAssistants, open, onItemSelectedFromKeyboard);

  const handlePopoverEntered = () => {
    if (paperRef.current && popoverMinWidth === 0) {
      const popoverWidth = paperRef.current.offsetWidth;
      setPopoverMinWidth(popoverWidth);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    resetFocus();
  };

  const handleMenuClose = () => {
    setSearchText("");
    setAnchorEl(null);
    setPopoverMinWidth(0);
    resetFocus();
  };

  const navigateToNewAssistant = assistantId => {
    let page;

    if (currentPage.includes(SKILLS)) {
      page = SKILLS;
    } else if (currentPage.includes(BUILDING_BLOCKS)) {
      page = BUILDING_BLOCKS;
    } else {
      page = currentPage;
    }
    navigate(`/${accountId}/${assistantId}/${page}`);
  };

  const handleSelectedAssistantChange = (assistantId) => {
    clearAssistant();
    clearPreviewState();
    deleteInsightsDashboardKey(selectedAssistant?.assistantId);

    if (isEditMode && !isFirstDraft) {
      setUpcomingAssistantId(assistantId);
      navigateToNewAssistant(assistantId);
    } else {
      setSelectedCollection(null);
      navigateToNewAssistant(assistantId);
    }
    handleMenuClose();
  };

  const renderNameAndChannel = (assistant, isList, selected) => {
    let channelText = CHANNEL_TO_DISPLAY_NAME[assistant?.channel] ? CHANNEL_TO_DISPLAY_NAME[assistant?.channel] + " Agent" : "Unknown Channel";
    if (assistant?.engagementType === ENGAGEMENT_TYPES.OUTBOUND) {
      channelText = `${capitalizeString(assistant?.engagementType?.toLowerCase())} ${channelText}`;
    }

    return (<TextContainer>
      <ListItemText style={{ marginBlock: 0 }}>
        <Typography variant="body2" noWrap>
          {assistant?.name}
        </Typography>
      </ListItemText>
      <ChannelListItem style={{ marginTop: isList ? "2px" : "4px" }} disableTypography>
        <Typography variant="caption" noWrap>
          <ChannelText noWrap isList={isList} selected={selected}>
            {channelText}
          </ChannelText>
        </Typography>
        {isList ? <Typography sx={{ ml: "4px" }} variant="caption" noWrap>• {ASSISTANT_TYPE_TO_METADATA[assistant?.type]?.displayName}</Typography> : <StyledChip
          label={ASSISTANT_TYPE_TO_METADATA[assistant?.type]?.displayName}
          textColorPath={ASSISTANT_TYPE_TO_METADATA[assistant?.type]?.textColorPath}
          backgroundColorPath={
            ASSISTANT_TYPE_TO_METADATA[assistant?.type]?.backgroundColorPath
          }
        />}
      </ChannelListItem>
    </TextContainer>);
  };

  const getTooltipText = useMemo(() => {
    const { name, engagementType, channel } = selectedAssistant || {};
    const channelDisplayName = CHANNEL_TO_DISPLAY_NAME[channel] || "Unknown Channel";

    if (!engagementType) {
      return `${name}, ${channelDisplayName} Agent`;
    }

    const formattedEngagementType = capitalizeString(engagementType.toLowerCase());
    return `${name}, ${formattedEngagementType} ${channelDisplayName} Agent`;
  }, [selectedAssistant]);

  const tooltipText = getTooltipText;

  return <>
    <ButtonContainer>
      <Tooltip
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: "180px",
              marginLeft: isNavPanelOpen ? "0px" : "8px"
            }
          }
        }}
        title={tooltipText} placement="bottom"
      >
        <StyledButton
          hover={buttonIsClickable}
          {...(buttonIsClickable && { onClick: handleMenuOpen })}
          disableRipple
        >
          {isNavPanelOpen ? renderNameAndChannel(selectedAssistant, false) : null}
          {buttonIsClickable && (
            <StyledButtonIcon sx={{ ml: isNavPanelOpen ? "8px" : "0px" }}>
              <UnfoldIcon fontSize="small" />
            </StyledButtonIcon>
          )}
        </StyledButton>
      </Tooltip>
    </ButtonContainer>

    <Popover
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      onKeyDown={handleKeyDown}
      elevation={3}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left"
      }}
      PaperProps={{
        ref: paperRef,
        style: {
          maxHeight: showSearch ? 400 : 300,
          maxWidth: 400,
          minWidth: popoverMinWidth
        }
      }}
    >
      <List
        ref={listRef}
        onMouseOver={handlePopoverEntered}
        style={{ outline: "none" }}
        sx={{ overflow: "auto" }}>
        {showSearch && (
          <StyledListItem>
            <SearchBar
              hideTooltip
              stayOpen
              shouldAutoFocus
              testId="agent-selector"
              onChange={setSearchText}
              customWidth="100%"
              handleKeyDown={handleKeyDown} />
          </StyledListItem>)
        }

        {assistantsToMap.map((assistant, i) => {
          const selected = assistant?.id === selectedAssistant?.assistantId;
          const icon = getAgentIcon(assistant);

          return <StyledListItem key={assistant?.id + i}>
            {assistant?.key === "no_options"
              ?
              <StyledListItemButton disabled>
                <Typography variant="body2">
                  {assistant?.display_key}
                </Typography>
              </StyledListItemButton>
              :
              <StyledListItemButton
                onClick={() => handleSelectedAssistantChange(assistant?.id)}
                selected={selected}
                focused={focusedIndex === i}
              >
                <ListItemIcon sx={{ minWidth: 0, marginRight: 1 }}>
                  <StyledIconContainer>
                    {icon && <img src={icon} alt="" width={22} height={22} />}
                  </StyledIconContainer>
                </ListItemIcon>
                {renderNameAndChannel(assistant, true, selected)}
                {selected && (
                  <StyledListItemIcon {...{ selected: true }}>
                    <CheckIcon />
                  </StyledListItemIcon>)}
              </StyledListItemButton>}
          </StyledListItem>;
        })}
      </List>
    </Popover>
    <Dialog
      open={isExitDialogOpen}
      title={EXIT_ASSISTANT_DIALOG.title}
      submitButtonText={EXIT_ASSISTANT_DIALOG.discardButton}
      cancelButtonText={EXIT_ASSISTANT_DIALOG.keepEditingButton}
      variant={DIALOG_VARIANTS.transactional}
      handleSubmit={() => {
        navigateToNewAssistant(upcomingAssistantId);
        handleMenuClose();
        setIsExitDialogOpen(false);
      }}
      handleCancel={() => setIsExitDialogOpen(false)}
    >
      {EXIT_ASSISTANT_DIALOG.content}
    </Dialog>
  </>;
};

export default AssistantSelector;
